declare global {
  interface Window {
    RulerAnalytics?: {
      trackConversionAsync: (
        payload: Record<string, any>,
        p2?: string,
        p3?: string,
        p4?: number
      ) => void
    }
  }
}

export default function initFormieListener() {
  // Prevent oversized values from being sent to Ruler
  const MAX_FIELD_VALUE_LENGTH = 2048

  // Generate UUID for Ruler (native if available, fallback otherwise)
  const generateUuid = (): string => {
    if (crypto?.randomUUID) return crypto.randomUUID()

    const hex = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
    return hex.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  // Ensure fields[rulerGuid] is always populated
  const setRulerGuidIfPresent = (root: ParentNode | Document = document) => {
    root
      .querySelectorAll<HTMLInputElement>('input[name="fields[rulerGuid]"]')
      .forEach((input) => {
        if (!input.value) {
          input.value = generateUuid()
          input.dispatchEvent(new Event('input', { bubbles: true }))
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
  }

  // Normalize string values
  const normalizeString = (v: any) => (typeof v === 'string' ? v.trim() : '')

  // Extract email regardless of Formie group depth
  const extractEmail = (flatFields: Record<string, any>) => {
    const emailKey = Object.keys(flatFields).find((k) =>
      /\[(email|emailAddress)\]$/i.test(k)
    )
    return emailKey ? normalizeString(flatFields[emailKey]) : ''
  }

  // Flatten FormData while excluding system fields
  const buildFlatFields = (form: HTMLFormElement) => {
    const formData = new FormData(form)
    const flatFields: Record<string, any> = {}

    formData.forEach((value, key) => {
      if (
        key === 'CRAFT_CSRF_TOKEN' ||
        key === 'action' ||
        key === 'siteId' ||
        key === 'submitAction' ||
        key === 'g-recaptcha-response' ||
        key === 'cf-turnstile-response'
      ) return

      if (typeof value === 'string' && value.length > MAX_FIELD_VALUE_LENGTH) return
      flatFields[key] = Array.isArray(value) ? value.join(', ') : value
    })

    return flatFields
  }

  // Mark that a real submit attempt occurred
  const markSubmitIntent = (form: HTMLFormElement) => {
    form.dataset.formieSubmitIntent = 'true'
    window.setTimeout(() => {
      delete form.dataset.formieSubmitIntent
    }, 1500)
  }

  // Centralized tracking handler (prevents duplicates)
  const trackForm = (
    form: HTMLFormElement,
    source: 'submit' | 'validate' | 'dom-submit'
  ) => {
    if (form.dataset.rulerTracked === 'true') return

    setRulerGuidIfPresent(form)

    const flatFields = buildFlatFields(form)
    const email = extractEmail(flatFields)

    if (!email) return

    form.dataset.rulerTracked = 'true'

    const rulerPayload = {
      ...flatFields,
      email,
      form_id: form.getAttribute('id'),
      form_handle: form.querySelector<HTMLInputElement>('input[name="handle"]')?.value,
      page_url: window.location.href,
      ruler_source: source,
    }

    if (window.RulerAnalytics) {
      window.RulerAnalytics.trackConversionAsync(rulerPayload, '', '', 500)
    }
  }

  // Populate rulerGuid on initial load
  setRulerGuidIfPresent(document)

  // Native submit listener (capture)
  // Marks intent and tracks only non-Formie (bespoke) forms
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null
      if (!form) return

      markSubmitIntent(form)

      const hasFormieHandle = !!form.querySelector('input[name="handle"]')
      if (!hasFormieHandle) {
        trackForm(form, 'dom-submit')
      }
    },
    true
  )

  // Preferred Formie hook (fires after successful submit)
  document.addEventListener('onAfterFormieSubmit', (e: Event) => {
    const form = (e.target as HTMLElement).closest('form') as HTMLFormElement | null
    if (!form) return
    trackForm(form, 'submit')
  })

  // Validate fallback (only after a real submit attempt)
  document.addEventListener('onAfterFormieValidate', (e: Event) => {
    const form = (e.target as HTMLElement).closest('form') as HTMLFormElement | null
    if (!form) return
    if (form.dataset.formieSubmitIntent !== 'true') return

    const ce = e as CustomEvent
    const isValid = ce?.detail?.isValid ?? true
    if (!isValid) return

    trackForm(form, 'validate')
  })
}
